import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fetchEvents } from '../api/public'
import { EventCard } from '../components/EventCard'
import { EventPromoHero } from '../components/EventPromoHero'
import { usePageMeta } from '../hooks/usePageMeta'
import { pickPromotedUpcoming } from '../lib/pick-promoted-event'
import { schemaBreadcrumb, schemaEventList, schemaGraph, schemaWebPage } from '../lib/schema-org'

type Tab = 'upcoming' | 'past' | 'cancelled'
const VALID_TABS: Tab[] = ['upcoming', 'past', 'cancelled']

/** Temporarily hide the featured promo hero on /events. Set true to re-enable. */
const SHOW_EVENTS_FEATURED_PROMO = false

export function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab') as Tab | null
  const tab: Tab = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'upcoming'
  const setTab = (t: Tab) => setSearchParams(t === 'upcoming' ? {} : { tab: t }, { replace: false })

  const upcomingQ = useQuery({
    queryKey: ['events', 'upcoming'],
    queryFn: () => fetchEvents({ status: 'upcoming' }),
  })

  const listQ = useQuery({
    queryKey: ['events', tab],
    queryFn: () => fetchEvents({ status: tab }),
    enabled: tab !== 'upcoming',
  })

  const listData = tab === 'upcoming' ? upcomingQ.data : listQ.data
  const rawList = listData?.events ?? []
  const listError = tab === 'upcoming' ? upcomingQ.isError : listQ.isError

  const promo = useMemo(
    () => pickPromotedUpcoming(upcomingQ.data?.events),
    [upcomingQ.data?.events]
  )

  const displayEvents = useMemo(() => {
    if (tab === 'upcoming') {
      return [...rawList].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
    }
    return rawList
  }, [rawList, tab])

  const showHero =
    SHOW_EVENTS_FEATURED_PROMO &&
    !upcomingQ.isLoading &&
    Boolean(promo && promo.displayStatus === 'upcoming' && new Date(promo.startsAt).getTime() > Date.now())

  const tabLabel = tab === 'upcoming' ? 'Demnächst' : tab === 'past' ? 'Vergangen' : 'Abgesagt'
  const jsonLd = useMemo(() => {
    const crumbs = schemaBreadcrumb(
      [
        { name: 'Home', path: '/' },
        { name: 'Events', path: '/events' },
      ],
      '/events',
    )
    return schemaGraph(
      crumbs,
      schemaWebPage({
        path: '/events',
        name: `Events — ${tabLabel}`,
        description: 'Demnächst, vergangene und abgesagte BNKR Events.',
        pageType: 'CollectionPage',
        breadcrumb: crumbs,
        mainEntity: schemaEventList(displayEvents),
      }),
      schemaEventList(displayEvents),
    )
  }, [displayEvents, tabLabel])

  usePageMeta({
    title: 'Events',
    description: 'Alle BNKR Events — demnächst, vergangen und abgesagt.',
    canonical: tab === 'upcoming' ? '/events' : `/events?tab=${tab}`,
    jsonLd,
  })

  return (
    <div className="wrap" style={{ paddingTop: 'var(--sp-8)', paddingBottom: 'var(--sp-16)' }}>
      <div className="sect-head">
        <div>
          <div className="kicker crt">↳ /events</div>
          <h1 className="display h2" style={{ marginTop: 14 }}>
            Events
          </h1>
        </div>
        <div className="lead" style={{ fontSize: 18, maxWidth: '50ch' }}>
          Demnächst und vergangene Termine.
        </div>
      </div>

      {showHero && promo ? (
        <div style={{ marginBottom: 40 }}>
            <EventPromoHero
              ev={promo}
              eventDetailPath={`/events/${promo.slug}`}
              linkTitleToDetail
              contentKindLabel="Events"
              showTerminalFeed={false}
            />
        </div>
      ) : null}

      <div className="tabs year-tabs">
        <button
          type="button"
          className={`tab year-tab ${tab === 'upcoming' ? 'active' : ''}`}
          onClick={() => setTab('upcoming')}
        >
          Demnächst
        </button>
        <button
          type="button"
          className={`tab year-tab ${tab === 'past' ? 'active' : ''}`}
          onClick={() => setTab('past')}
        >
          Vergangen
        </button>
        <button
          type="button"
          className={`tab year-tab ${tab === 'cancelled' ? 'active' : ''}`}
          onClick={() => setTab('cancelled')}
        >
          Abgesagt
        </button>
      </div>

      <section style={{ marginTop: 24 }}>
        {listError && <p className="error">Events konnten nicht geladen werden.</p>}
        {(tab === 'upcoming' ? upcomingQ.isLoading : listQ.isLoading) && (
          <p className="muted" style={{ fontFamily: 'var(--mono)', letterSpacing: '.14em', fontSize: 13 }}>
            LOADING…
          </p>
        )}
        {listData && rawList.length === 0 && <p className="muted">Keine Einträge in dieser Ansicht.</p>}
        <div className="ev-list" role="list" aria-label="Events">
          {displayEvents.map((ev) => (
            <EventCard key={ev.id} ev={ev} />
          ))}
        </div>
      </section>
    </div>
  )
}
